import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    LinearProgress,
    Alert,
    Chip,
    Divider,
    Container
} from '@mui/material';
import {
    Verified as VerifiedIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    School as CourseIcon,
    Person as PersonIcon,
    CalendarToday as CalendarIcon,
    Stars as ScoreIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { CertificateVerification, certificateService } from '../../services/certificateService';

const VerifyPage: React.FC = () => {
    const { code } = useParams<{ code: string }>();
    const [loading, setLoading] = useState(true);
    const [verification, setVerification] = useState<CertificateVerification | null>(null);

    useEffect(() => {
        if (code) {
            verifyCertificate(code);
        }
    }, [code]);

    const verifyCertificate = async (verificationCode: string) => {
        setLoading(true);
        try {
            const result = await certificateService.verifyCertificate(verificationCode);
            setVerification(result);
        } catch (error) {
            console.error('Verification error:', error);
            setVerification({
                valid: false,
                expired: false,
                message: 'Verification failed'
            });
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="h5" gutterBottom>
                            Zertifikat wird verifiziert...
                        </Typography>
                        <LinearProgress sx={{ mt: 3 }} />
                    </CardContent>
                </Card>
            </Container>
        );
    }

    if (!verification || !verification.certificate) {
        return (
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                        <Typography variant="h5" gutterBottom color="error">
                            Zertifikat nicht gefunden
                        </Typography>
                        <Typography color="text.secondary">
                            Das Zertifikat mit dem Code <strong>{code}</strong> konnte nicht gefunden werden.
                        </Typography>
                        <Alert severity="warning" sx={{ mt: 3, textAlign: 'left' }}>
                            Bitte überprüfen Sie den Verifizierungscode und versuchen Sie es erneut.
                        </Alert>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    const cert = verification.certificate;

    return (
        <Container maxWidth="sm" sx={{ py: 8 }}>
            <Card
                sx={{
                    borderTop: 6,
                    borderColor: verification.valid ? 'success.main' : 'error.main'
                }}
            >
                <CardContent sx={{ py: 4 }}>
                    {/* Status Section */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        {verification.valid ? (
                            <>
                                <VerifiedIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                                <Typography variant="h4" gutterBottom color="success.main">
                                    Zertifikat gültig
                                </Typography>
                                <Chip
                                    icon={<VerifiedIcon />}
                                    label="Verifiziert"
                                    color="success"
                                    size="medium"
                                />
                            </>
                        ) : (
                            <>
                                <WarningIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                                <Typography variant="h4" gutterBottom color="error">
                                    Zertifikat abgelaufen
                                </Typography>
                                <Chip
                                    icon={<WarningIcon />}
                                    label="Abgelaufen"
                                    color="error"
                                    size="medium"
                                />
                            </>
                        )}
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Certificate Details */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <PersonIcon color="action" />
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Inhaber
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {cert.userName}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CourseIcon color="action" />
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Kurs
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {cert.courseName}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CalendarIcon color="action" />
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Ausgestellt am
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {certificateService.formatDate(cert.issuedAt)}
                                </Typography>
                            </Box>
                        </Box>

                        {cert.expiresAt && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <CalendarIcon color={verification.expired ? 'error' : 'action'} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Gültig bis
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        fontWeight="medium"
                                        color={verification.expired ? 'error.main' : 'text.primary'}
                                    >
                                        {certificateService.formatDate(cert.expiresAt)}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {cert.score && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <ScoreIcon color="action" />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Erreichte Punktzahl
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {cert.score}%
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Verification Code */}
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                            Verifizierungscode
                        </Typography>
                        <Typography
                            variant="h6"
                            fontFamily="monospace"
                            sx={{ letterSpacing: 2 }}
                        >
                            {cert.verificationCode}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            {/* Footer */}
            <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ mt: 3 }}
            >
                Dieses Zertifikat wurde von der Online Academy ausgestellt.
            </Typography>
        </Container>
    );
};

export default VerifyPage;
